// The Library panel: the account's objects (Eden) and this computer's, by category.
// Click one to place it. B (or "Save selection…") adds the selection.
// Find: words typed (any order), filters (show, pump data, fitting data) and favourites (a star on each
// object, kept in this browser). The rules are in library/filter.ts and library/favourites.ts.
import { useEffect, useMemo, useState } from "preact/hooks";
import type { ToolManager } from "../tools/toolManager";
import { categoryChoices, groupByCategory, thumbnailSvg, type LibraryItem, type LibrarySource } from "../library/library";
import { moveToCategory, usedCategories } from "../library/save";
import { CategoryPicker } from "./categoryPicker";
import { loadAccountCache, loadLocalItems, removeLocalItem } from "../library/store";
import { builtInFittings } from "../library/fittings";
import { builtInLintPots } from "../library/lintPots";
import { builtInCheckValves } from "../library/checkValves";
import { builtInButterflyValves } from "../library/butterflyValves";
import { builtInChlorinators } from "../library/chlorinators";
import { FAVS_KEY, loadFavs, removeFav, saveFavs, toggleFav } from "../library/favourites";
import { NO_FILTER, describeCounts, facetOptions, filterRows, isFiltering, kindsPresent, summarise, type FilterState } from "../library/filter";
import { LibraryFilters } from "./libraryFilters";
import { toasts } from "./toasts";

export const LIBRARY_CHANGED = "tracecad:library-changed";

interface Row {
  item: LibraryItem;
  /** builtin = the pipe fittings and Davey pumps that come with TraceCAD: always there, cannot be removed. */
  source: LibrarySource | "builtin";
  edenId?: string;
}

const BUILT_IN: Row[] = [...builtInFittings(), ...builtInLintPots(), ...builtInCheckValves(), ...builtInButterflyValves(), ...builtInChlorinators()].map((item) => ({ item, source: "builtin" as const }));

/** A thumbnail is drawn once for each object: an object never changes, and 300 of them are on screen at times. */
const thumbs = new WeakMap<LibraryItem, string>();
const thumbOf = (item: LibraryItem): string => {
  let s = thumbs.get(item);
  if (s === undefined) {
    s = thumbnailSvg(item);
    thumbs.set(item, s);
  }
  return s;
};

/** 3.30.0: the move button: a folder with an arrow. Drawn here, not a text character. */
function MoveIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3 6.5h6l2 2h10v10.5H3z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
      <path d="M9 13.5h7m-2.5-2.5 2.5 2.5-2.5 2.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

/** The star: outline when off, filled when on. Drawn here, not a text character. */
function Star({ on }: { on: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <polygon
        points="12,2.2 14.9,8.6 21.9,9.3 16.6,13.9 18.2,20.8 12,17.2 5.8,20.8 7.4,13.9 2.1,9.3 9.1,8.6"
        fill={on ? "currentColor" : "none"}
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linejoin="round"
      />
    </svg>
  );
}

const rowKey = (r: Row) => `${r.source}:${r.item.id}`;
const flip = (s: Set<string>, v: string): Set<string> => {
  const n = new Set(s);
  if (n.has(v)) n.delete(v);
  else n.add(v);
  return n;
};

export function LibraryPanel({ tools }: { tools: ToolManager }) {
  const [rows, setRows] = useState<Row[]>(() => loadLocalItems().map((item) => ({ item, source: "local" as const })));
  const [account, setAccount] = useState<{ id: string } | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [fs, setFs] = useState<FilterState>(NO_FILTER);
  const [favs, setFavs] = useState<string[]>(() => loadFavs());
  const [confirm, setConfirm] = useState<string | null>(null);
  // 3.30.0: the object whose category is being changed, and the category chosen
  const [moving, setMoving] = useState<string | null>(null);
  const [moveTo, setMoveTo] = useState("");
  const [movingBusy, setMovingBusy] = useState(false);
  const [nonce, setNonce] = useState(0);
  // The 272 Davey pump objects (plan and side of 136 pumps) are a separate chunk: read when the panel opens.
  const [pumps, setPumps] = useState<Row[]>([]);
  useEffect(() => {
    let live = true;
    void import("../library/pumps").then((m) => {
      if (live) setPumps(m.builtInPumps().map((item) => ({ item, source: "builtin" as const })));
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    const again = () => setNonce((v) => v + 1);
    window.addEventListener(LIBRARY_CHANGED, again);
    return () => window.removeEventListener(LIBRARY_CHANGED, again);
  }, []);

  // A change of the favourites in another tab of this browser.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === FAVS_KEY || e.key === null) setFavs(loadFavs());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    let live = true;
    const local: Row[] = loadLocalItems().map((item) => ({ item, source: "local" }));
    setRows(local);
    void (async () => {
      const eden = await import("../eden");
      const cfg = await eden.loadEdenConfig();
      if (!live) return;
      if (!cfg) {
        setAccount(null);
        setStatus(null);
        return;
      }
      setAccount({ id: cfg.accountId });
      // The cached copy first, so the panel is never empty while Eden answers.
      const cached = loadAccountCache(cfg.accountId).map((r) => ({ item: r.item, source: "account" as const, edenId: r.edenId }));
      setRows([...cached, ...local]);
      setStatus("Checking the account library…");
      try {
        const fresh = await eden.listAccountLibrary(cfg);
        if (!live) return;
        setRows([...fresh.map((r) => ({ item: r.item, source: "account" as const, edenId: r.edenId })), ...local]);
        setStatus(null);
      } catch (e) {
        if (live) setStatus(`Account library not reached: ${(e as Error).message}. Showing the last copy.`);
      }
    })();
    return () => {
      live = false;
    };
  }, [nonce]);

  const favSet = useMemo(() => new Set(favs), [favs]);
  const [warned, setWarned] = useState(false);
  const star = (r: Row) => {
    const next = toggleFav(favs, r.item.id);
    setFavs(next);
    if (!saveFavs(next) && !warned) {
      setWarned(true);
      toasts.push("info", "This browser does not let TraceCAD keep favourites. They last until you close this page.");
    }
  };

  const all = useMemo(() => [...rows, ...BUILT_IN, ...pumps], [rows, pumps]);
  const shown = useMemo(() => filterRows(all, fs, favSet), [all, fs, favSet]);
  const options = useMemo(() => facetOptions(all, fs, favSet), [all, fs, favSet]);
  const present = useMemo(() => kindsPresent(all), [all]);
  const filtering = isFiltering(fs);
  const counts = describeCounts(summarise(all, shown, favSet), filtering);
  const totals = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of all) m.set(r.item.category, (m.get(r.item.category) ?? 0) + 1);
    return m;
  }, [all]);

  // Folded groups: the person's choice while nothing is typed or set; a separate choice while a search or
  // a filter is active (every group opens by itself then), so the first choice comes back when it is cleared.
  const [openCats, setOpenCats] = useState<Set<string>>(() => new Set());
  const [shut, setShut] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!filtering) setShut((s) => (s.size ? new Set() : s));
  }, [filtering]);

  const favRows = useMemo(
    () => (favSet.size ? shown.filter((r) => favSet.has(r.item.id)).sort((a, b) => a.item.name.localeCompare(b.item.name, undefined, { numeric: true })) : []),
    [shown, favSet]
  );
  // With "Show: Favourites" the Favourites group is the whole list: the objects are not repeated below it.
  const groups = useMemo(() => (fs.show === "favourites" ? [] : groupByCategory(shown)), [shown, fs.show]);

  const remove = async (r: Row) => {
    setConfirm(null);
    if (r.source === "local") {
      removeLocalItem(r.item.id);
    } else if (r.edenId) {
      try {
        const eden = await import("../eden");
        const cfg = await eden.loadEdenConfig();
        if (cfg) await eden.removeAccountItem(cfg, r.edenId);
      } catch (e) {
        toasts.push("error", `Not removed: ${(e as Error).message}`);
        return;
      }
    }
    if (favSet.has(r.item.id)) {
      const next = removeFav(favs, r.item.id);
      setFavs(next);
      saveFavs(next);
    }
    toasts.push("success", `"${r.item.name}" removed from the library.`);
    setNonce((v) => v + 1);
  };

  // 3.30.0: the categories to move an object to: the standard ones and every one in use
  const moveChoices = useMemo(() => categoryChoices(usedCategories(rows.map((r) => r.item))), [rows]);
  const move = async (r: Row) => {
    setMovingBusy(true);
    const why = await moveToCategory(r.item, r.source === "account" ? "account" : "local", moveTo);
    setMovingBusy(false);
    if (why) {
      toasts.push("error", why);
      return;
    }
    setMoving(null);
    toasts.push("success", `"${r.item.name}" moved to ${moveTo.trim()}.`);
    setNonce((v) => v + 1);
  };

  const renderRow = (r: Row) => {
    const fav = favSet.has(r.item.id);
    const label = fav ? "Remove from favourites" : "Add to favourites";
    return (
      <div class="library-item" key={rowKey(r)}>
        <button class="library-pick" onClick={() => tools.startPlace(r.item)} title={`Place ${r.item.name} — click in the drawing, as many times as you need`}>
          <span class="library-thumb" dangerouslySetInnerHTML={{ __html: thumbOf(r.item) }} />
          <span class="library-name">{r.item.name}</span>
          <span class="library-size muted">
            {Math.round(r.item.size.w)} × {Math.round(r.item.size.h)} mm{r.source === "local" ? " · this computer" : r.source === "builtin" ? " · built in" : ""}
          </span>
        </button>
        <button type="button" class={`library-star${fav ? " on" : ""}`} aria-label={label} title={`${label}: ${r.item.name}`} onClick={() => star(r)}>
          <Star on={fav} />
        </button>
        {r.source !== "builtin" && moving !== rowKey(r) && (
          <button
            type="button"
            class="library-move-btn"
            aria-label={`Move to another category: ${r.item.name}`}
            title={`Move to another category (now ${r.item.category})`}
            onClick={() => {
              setConfirm(null);
              setMoveTo(r.item.category);
              setMoving(rowKey(r));
            }}
          >
            <MoveIcon />
          </button>
        )}
        {r.source !== "builtin" && moving === rowKey(r) && (
          <div class="library-move" role="group" aria-label={`Move ${r.item.name} to another category`}>
            <small class="muted">Move to</small>
            <CategoryPicker id="library-move-cat" value={moveTo} choices={moveChoices} onChange={setMoveTo} />
            <span class="library-move-buttons">
              <button id="library-move-ok" class="primary" disabled={movingBusy} onClick={() => void move(r)}>
                {movingBusy ? "Moving…" : "Move"}
              </button>
              <button onClick={() => setMoving(null)}>Cancel</button>
            </span>
          </div>
        )}
        {r.source === "builtin" || moving === rowKey(r) ? null : confirm === rowKey(r) ? (
          <span class="library-confirm">
            <button class="danger" onClick={() => void remove(r)}>
              Remove
            </button>
            <button onClick={() => setConfirm(null)}>Keep</button>
          </span>
        ) : (
          <button class="library-x" title="Remove from the library" onClick={() => setConfirm(rowKey(r))}>
            ×
          </button>
        )}
      </div>
    );
  };

  return (
    <div class="library">
      <div class="library-bar">
        <input
          type="search"
          id="library-filter"
          placeholder="Find an object"
          value={fs.text}
          onInput={(e) => {
            const text = (e.target as HTMLInputElement).value;
            setFs((s) => ({ ...s, text }));
          }}
          onKeyDown={(e) => e.stopPropagation()}
        />
        <button onClick={() => tools.openPrompt("save")} title="Save the selected objects as a library object (B)">
          Save selection…
        </button>
        <button
          id="library-dxf"
          title="Read a 2D DXF drawing (a pump, a filter, a fitting) and keep it as a library object"
          onClick={() => {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = ".dxf,.dwg";
            input.multiple = true;
            input.onchange = () => {
              if (input.files?.length) window.dispatchEvent(new CustomEvent("tracecad:dxf", { detail: [...input.files] }));
            };
            input.click();
          }}
        >
          Import DXF…
        </button>
      </div>
      <LibraryFilters state={fs} options={options} present={present} counts={counts} onChange={setFs} />
      <small class="muted">
        {account ? "Account library: everyone in this Eden account sees it." : "This computer only. Connect Eden to share a library with the account."}
      </small>
      {status && <small class="muted library-status">{status}</small>}
      {!filtering && groups.every((g) => g.rows.every((r) => r.source === "builtin")) && (
        <p class="muted library-empty">
          No objects of your own yet. Draw a pump, a filter or a manifold, select it, and press <kbd>B</kbd>. Or press <b>Import DXF…</b> to read one from a CAD file.
        </p>
      )}
      {shown.length === 0 && (
        <p class="muted library-none" data-empty="">
          No object matches. Change the words or the filters, or press <b>Clear filters</b>.
        </p>
      )}
      {favRows.length > 0 && (
        <div class="library-group library-favs" key="favourites" data-category="Favourites">
          <div class="library-cat">
            <span class="library-cat-star">
              <Star on={true} />
            </span>{" "}
            Favourites <span class="muted">· {favRows.length}</span>
          </div>
          <div class="library-grid">{favRows.map(renderRow)}</div>
        </div>
      )}
      {groups.map((g) => {
        // the built-in pipe fittings and pumps fold away so they never bury the account's own objects
        const builtin = g.rows.every((r) => r.source === "builtin");
        const open = !builtin || (filtering ? !shut.has(g.category) : openCats.has(g.category));
        const total = totals.get(g.category) ?? g.rows.length;
        return (
          <div class={`library-group${builtin ? " library-builtin" : ""}`} key={g.category} data-category={g.category}>
            {builtin ? (
              <button
                class="library-cat library-fold"
                aria-expanded={open}
                onClick={() => (filtering ? setShut((s) => flip(s, g.category)) : setOpenCats((s) => flip(s, g.category)))}
              >
                <span aria-hidden="true">{open ? "▾" : "▸"}</span> {g.category}{" "}
                <span class="muted">· {filtering && total !== g.rows.length ? `${g.rows.length} of ${total}` : g.rows.length}</span>
              </button>
            ) : (
              <div class="library-cat">{g.category}</div>
            )}
            {open && <div class="library-grid">{g.rows.map(renderRow)}</div>}
          </div>
        );
      })}
    </div>
  );
}
